import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Session,
  ValidateSessionSuccessPayload,
} from "../../types/session.definition";
import { DEFAULT_PIN_LOCK_MINUTES } from "../../utils/pin-utils";
import { RootState } from "../types";

interface SessionState {
  session: Session | null;
  expiresAt: string | null;
  loading: boolean;
  error: string | null;
  sessionId: string | null;
  sessionStatus: "INVALID_SESSION" | "VALID_SESSION" | null;
  /**
   * MED-1622: whether the user has a PIN. `null` while unknown, and when the
   * API predates the PIN feature: the lock stays off in that case.
   */
  hasPin: boolean | null;
  /** The server says the session is locked (session payload or a 423). */
  locked: boolean;
  /**
   * MED-1622: the server ended the session for a security reason
   * (PIN_CREATE_REAUTH_REQUIRED, SESSION_REUSE_DETECTED). The app explains it,
   * then sends the user to login.
   */
  securityLogout: SecurityLogoutCode | null;
}

export type SecurityLogoutCode =
  | "PIN_CREATE_REAUTH_REQUIRED"
  | "SESSION_REUSE_DETECTED";

type PinFlags = { hasPin?: boolean; locked?: boolean };

/** Only a boolean coming from the server changes the PIN state. */
const applyPinFlags = (state: SessionState, flags: PinFlags) => {
  if (typeof flags.hasPin === "boolean") state.hasPin = flags.hasPin;
  if (typeof flags.locked === "boolean") state.locked = flags.locked;
};

const initialState: SessionState = {
  session: null,
  expiresAt: null,
  loading: true,
  error: null,
  sessionId: null,
  sessionStatus: null,
  hasPin: null,
  locked: false,
  securityLogout: null,
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    validateSessionRequest: state => {
      state.loading = true;
      state.error = null;
    },
    validateSessionSuccess: (
      state,
      action: PayloadAction<ValidateSessionSuccessPayload>
    ) => {
      state.error = null;
      state.sessionId = action.payload.sessionId;
      state.expiresAt = action.payload.expiresAt;
      state.sessionStatus = action.payload.sessionStatus;
      applyPinFlags(state, action.payload);
    },
    validateSessionFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.sessionId = null;
      state.expiresAt = null;
      state.sessionStatus = "INVALID_SESSION";
    },
    getSessionRequest: state => {
      state.error = null;
    },
    getSessionSuccess: (state, action: PayloadAction<Session>) => {
      state.loading = false;
      state.session = action.payload;
      state.error = null;
      applyPinFlags(state, action.payload);
    },
    getSessionFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.sessionStatus = "INVALID_SESSION";
    },
    clearSession: state => {
      state.session = null;
      state.loading = false;
      state.error = null;
      state.hasPin = null;
      state.locked = false;
    },
    /** A call answered 423 SESSION_LOCKED: only a user with a PIN is locked. */
    sessionLocked: state => {
      state.hasPin = true;
      state.locked = true;
    },
    /** A call answered 403 PIN_REQUIRED: the user must create a PIN first. */
    pinRequired: state => {
      state.hasPin = false;
      state.locked = false;
    },
    /** PIN created, typed, changed or reset: the session is usable again. */
    pinAccepted: state => {
      state.hasPin = true;
      state.locked = false;
    },
    /** MED-1644: the user saved a new lock delay; the idle timer follows it. */
    pinLockMinutesChanged: (state, action: PayloadAction<number>) => {
      if (state.session) state.session.pinLockMinutes = action.payload;
    },
    /** Kept through clearSession, so the explanation stays until the redirect. */
    securityLogoutRequired: (
      state,
      action: PayloadAction<SecurityLogoutCode>
    ) => {
      state.securityLogout = action.payload;
    },
    requestTerminateSessionRequest: state => {
      state.loading = true;
    },
    expireSession: state => {
      state.sessionStatus = "INVALID_SESSION";
      state.session = null;
      state.sessionId = null;
      state.expiresAt = null;
      state.loading = false;
      state.error = "Session expired";
    },
  },
});

export const {
  validateSessionRequest,
  validateSessionSuccess,
  validateSessionFailure,
  getSessionRequest,
  getSessionSuccess,
  getSessionFailure,
  clearSession,
  sessionLocked,
  pinRequired,
  pinAccepted,
  pinLockMinutesChanged,
  securityLogoutRequired,
  requestTerminateSessionRequest,
  expireSession,
} = sessionSlice.actions;

export default sessionSlice.reducer;
export const selectSessionStaticData = (state: RootState) => {
  return {
    sessionStatus: state.session.sessionStatus,
    expiresAt: state.session.expiresAt,
    sessionId: state.session.sessionId,
  };
};

export const selectSessionAuthState = (state: RootState) => {
  return {
    sessionStatus: state.session.sessionStatus,
    loading: state.session.loading,
    session: state.session.session,
    expiresAt: state.session.expiresAt,
  };
};

export const selectHasPin = (state: RootState) => state.session.hasPin;
/** MED-1644: the user's lock delay in minutes, 15 when the session has none. */
export const selectPinLockMinutes = (state: RootState) =>
  state.session.session?.pinLockMinutes ?? DEFAULT_PIN_LOCK_MINUTES;
export const selectSessionLocked = (state: RootState) => state.session.locked;
export const selectSecurityLogout = (state: RootState) =>
  state.session.securityLogout;
